import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, ToastContext, RadioLabel, Message } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import Inventory from '../../models/inventory';
import SelectInventories from '../../components/inventories/SelectInventories';

export default function PartnersReservationModal({ organization, open = false, onClose = () => {}, setExportUrl, setFinished, finished }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [exportType, setExportType] = React.useState('all_inventories');
    const [selectedInventory, setSelectedInventory] = React.useState([]);
    const [inventoriesResponse, setInventoriesResponse] = React.useState(null);
    const [loadingInventories, setLoadingInventories] = React.useState(false);

    React.useEffect(() => {
        if (!open || !organization?.id) {
            return;
        }

        setLoadingInventories(true);
        Inventory.forOrganizations({ organizationId: organization.id })
            .then(response => {
                setInventoriesResponse(response);
                setLoadingInventories(false);
            })
            .catch(error => {
                console.error('Failed to load inventories:', error);
                toast.error(t('ticket.partnersList.download_error'));
                setLoadingInventories(false);
            });
    }, [open, organization?.id]);

    const inventories = React.useMemo(() => {
        if (!inventoriesResponse || !Array.isArray(inventoriesResponse)) {
            return [];
        }

        return inventoriesResponse.filter(inv => inv?.uuid);
    }, [inventoriesResponse]);
    const handleConfirm = React.useCallback(() => {
        setIsSubmitting(true);

        const url = `/ticket/organizations/${organization.id}/export/`;

        let data = {};
        if (exportType === 'select_inventories' && selectedInventory && selectedInventory.length > 0) {
            data.inventories_uuids = selectedInventory;
        }

        const payload = {
            status: 'ready',
            process: 'partner_bookings',
            data: data
        };

        fetcher
            .post(url, { data: payload })
            .then(({ response, status }) => {
                if (status !== 201) {
                    throw new Error('Export could not be created');
                }

                if (response.status === 'ready' && response.url) {
                    setExportUrl(response.url);
                    setFinished(false);
                    toast.info(t('ticket.export.download_preparation'));
                    setIsSubmitting(false);
                    onClose();
                }
            })
            .catch(() => {
                toast.error(t('ticket.export.download_preparation_error'));
                setIsSubmitting(false);
            });
    }, [exportType, selectedInventory, organization, onClose, setExportUrl, setFinished]);

    const handleClose = React.useCallback(() => {
        setExportType('all_inventories');
        setSelectedInventory([]);
        onClose();
    }, [onClose]);

    const handleExportTypeChange = React.useCallback((e, { value }) => {
        setExportType(value);
    }, []);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="large"
            title={t('ticket.partnersList.export_reservations_partner.title')}
            subtitle={t('ticket.partnersList.export_reservations_partner.subtitle')}
            customFooter={
                <Button.Group>
                    <Button onClick={handleClose} disabled={isSubmitting || !finished} label={t('ticket.common.action.cancel')} />
                    <Button primary isLoading={isSubmitting} disabled={!finished} onClick={handleConfirm} label={t('ticket.common.action.confirm')} />
                </Button.Group>
            }
        >
            <RadioLabel bordered inline name="exportType" value="all_inventories" checked={exportType === 'all_inventories'} onChange={handleExportTypeChange}>
                {t('ticket.partnersList.export_reservations_partner.all_active_events')}
            </RadioLabel>
            <RadioLabel bordered inline name="exportType" value="select_inventories" checked={exportType === 'select_inventories'} onChange={handleExportTypeChange}>
                {t('ticket.partnersList.export_reservations_partner.select_event')}
            </RadioLabel>

            {exportType === 'select_inventories' && (
                <Form.Field>
                    <SelectInventories
                        multi={true}
                        value={selectedInventory || []}
                        onChange={value => setSelectedInventory(value)}
                        inventories={inventories}
                        disabled={loadingInventories || isSubmitting}
                        loading={loadingInventories}
                        required={true}
                    />
                </Form.Field>
            )}
            {!loadingInventories && !inventories.length && (
                <Message type={'warning'} title={t('ticket.partnersList.export_reservations_partner.warning_title')}>
                    {t('ticket.partnersList.export_reservations_partner.warning_message')}
                </Message>
            )}
        </Modal>
    );
}
